import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as yup from "yup";
import Select from "react-select";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formValidate } from "helpers/form_helper";
import { getInitCustomerParams } from "pages/CustomerManagement/utils";
import { useListCustomerByKeySearch } from "hooks";

const ChangeCustomerForm = props => {
  const {
    isOpenModal,
    toggleModal,
    handleChangeOrderCustomer,
    isLoadingChangeOrderCustomer,
  } = props;

  const { t } = useTranslation(["order"]);
  let timeSearchCustomer;

  const getInitDefaultValues = () => {
    return {
      customer_id: undefined,
      note: "",
    };
  };

  const schema = yup.object().shape({
    customer_id: yup
      .object()
      .required(t("form.validate.select", { title: t("form.customer") })),
    note: yup
      .string()
      .required(t("form.validate.required", { title: t("form.reason") })),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  // List Customer
  const [customerParams, setCustomerParams] = useState(getInitCustomerParams);
  const { data: customers, refetch: listCustomer } = useListCustomerByKeySearch(
    {
      params: customerParams,
    }
  );

  // Modal Closed
  useEffect(() => {
    if (!isOpenModal) {
      reset(getInitDefaultValues());
      setCustomerParams(getInitCustomerParams());
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (customerParams && customerParams.run) {
      listCustomer();
    }
  }, [customerParams]);

  useEffect(() => {
    if (isOpenModal) {
      setCustomerParams(prev => ({
        ...prev,
        sizePerPage: 100,
        shipping_type: "1,2,3",
        run: true,
      }));
    }
  }, [isOpenModal]);

  const onSubmit = _values => {
    const data = getValues();

    handleChangeOrderCustomer({
      customer_id: String(data.customer_id.value),
      note: data.note,
    });
  };

  const onSearchCustomer = (keywords, actions) => {
    if (actions.action === "input-change") {
      clearTimeout(timeSearchCustomer);
      timeSearchCustomer = setTimeout(() => {
        setCustomerParams(prev => ({
          ...prev,
          keywords: String(keywords).trim().replaceAll(" ", "+"),
        }));
      }, 500);
    }
  };

  return (
    <React.Fragment>
      <Modal
        size="lg"
        isOpen={isOpenModal}
        role="dialog"
        autoFocus={true}
        centered={true}
        toggle={toggleModal}
      >
        <ModalHeader toggle={toggleModal}>
          {t("form.change_customer")}
        </ModalHeader>
        <ModalBody>
          <Row className="row-cols-1">
            <Col>
              <FormGroup className="mb-3">
                <Label htmlFor="customer_id">
                  {t("form.customer")} <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={control}
                  name="customer_id"
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder={t("placeholder.select", {
                        title: t("form.customer"),
                      })}
                      // isSearchable
                      options={
                        customers?.items?.length > 0
                          ? customers.items.map(item => {
                              return {
                                label: item.name,
                                value: item.id,
                              };
                            })
                          : []
                      }
                      classNamePrefix="select2-selection"
                      onChange={data => {
                        field.onChange(data);
                      }}
                      onInputChange={(value, actions) => {
                        onSearchCustomer(value, actions);
                      }}
                      onBlur={() =>
                        onSearchCustomer("", { action: "input-change" })
                      }
                    />
                  )}
                />
                {errors.customer_id && (
                  <MessageFormValidate error={errors.customer_id.message} />
                )}
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label htmlFor="note">
                  {t("form.reason")} <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={control}
                  name="note"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      className={`form-control ${formValidate(
                        errors,
                        touchedFields,
                        "note"
                      )}`}
                      placeholder={t("placeholder.note")}
                      rows={5}
                    />
                  )}
                />
                {errors.note && (
                  <MessageFormValidate error={errors.note.message} />
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-block">
          <Row className="justify-content-end">
            <Col className="col-auto me-auto">
              <Button
                disabled={isLoadingChangeOrderCustomer}
                color="danger"
                outline
                className="border-0"
                size="xs"
                onClick={toggleModal}
              >
                {t("button.cancel")}
              </Button>
            </Col>
            <Col className="col-auto">
              <Button
                disabled={isLoadingChangeOrderCustomer}
                size="xs"
                color="primary"
                style={{ backgroundColor: "#3C64B1" }}
                onClick={() => handleSubmit(onSubmit)()}
              >
                {t("button.confirm")}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

ChangeCustomerForm.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  handleChangeOrderCustomer: PropTypes.func.isRequired,
  isLoadingChangeOrderCustomer: PropTypes.bool,
};

ChangeCustomerForm.defaultProps = {
  isOpenModal: false,
  dataDetail: {},
  isLoadingChangeOrderCustomer: false,
};

export default ChangeCustomerForm;
